import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Quote, Undo2, Redo2, Type } from 'lucide-react';

// Lightweight rich-text editor used across Itinerary builder fields:
// Notes, Terms & Conditions, Payment Policy, Day descriptions.
// Stores HTML directly in the existing string fields (backward compatible —
// any plain-text legacy value is wrapped in a <p> by Tiptap on first edit).
//
// BUG FIX — `extensions` MUST be a stable, module-level reference (per
// Tiptap v3's own React guide). Building `[StarterKit.configure(...)]` fresh
// inline inside the component body creates a NEW array every render; Tiptap
// v3's useEditor treats that as an options change and destroys + recreates
// the whole ProseMirror view, which drops DOM focus — so every keystroke
// (which re-renders the parent via onChange/setState) kicked focus out.
const EXTENSIONS = [StarterKit.configure({ heading: { levels: [2, 3] } })];
const ToolbarBtn = ({ onClick, active, title, children, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    data-testid={testId}
    className={`px-2 h-8 rounded-md text-sm flex items-center justify-center transition-colors border ${
      active
        ? 'bg-slate-900 text-white border-slate-900'
        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder = 'Type here…', minHeight = 120, testId }) {
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value || '',
    editorProps: {
      attributes: {
        class: 'prose prose-slate prose-sm sm:prose-base max-w-none focus:outline-none px-3 py-2 prose-li:my-0.5 prose-ul:pl-5 prose-ol:pl-5 prose-headings:font-heading prose-h2:text-xl prose-h3:text-lg prose-p:my-1.5',
        'data-testid': testId ? `${testId}-area` : undefined,
        style: `min-height:${minHeight}px;`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Treat empty <p></p> as empty string for backward storage compat.
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Sync external value changes (e.g., when API response arrives after mount)
  useEffect(() => {
    if (!editor) return;
    if (value === undefined || value === null) return;
    if (editor.getHTML() === value) return;
    editor.commands.setContent(value || '', { emitUpdate: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testId}>
      <div className="flex flex-wrap gap-1 p-2 border-b border-slate-100 bg-slate-50/60 rounded-t-xl">
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
          testId="rte-bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
          testId="rte-italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().setParagraph().run()}
          active={editor.isActive('paragraph')}
          title="Normal text"
          testId="rte-paragraph"
        >
          <Type className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading (large)"
          testId="rte-heading"
        >
          <Heading2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Subheading"
          testId="rte-heading-3"
        >
          <Heading3 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <span className="w-px bg-slate-200 mx-1" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bulleted list"
          testId="rte-bullet"
        >
          <List className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered list"
          testId="rte-ordered"
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Quote"
          testId="rte-quote"
        >
          <Quote className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <span className="w-px bg-slate-200 mx-1" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().undo().run()}
          active={false}
          title="Undo"
          testId="rte-undo"
        >
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().redo().run()}
          active={false}
          title="Redo"
          testId="rte-redo"
        >
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
      </div>
      <div className="relative">
        <EditorContent editor={editor} />
        {editor.isEmpty && (
          <span className="absolute top-2 left-3 text-sm text-slate-400 pointer-events-none">{placeholder}</span>
        )}
      </div>
    </div>
  );
}

// Helper used in Preview pages to detect HTML content vs plain text and render
// safely. Keeps backward-compatibility with legacy plain-text values.
export function isHtmlString(s) {
  return typeof s === 'string' && /<\/?[a-z][\s\S]*>/i.test(s);
}
